import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Using lucide-react icons for a consistent and clean look
import {
  Server,
  AlertCircle,
  CheckCircle,
  FileText,
  X,
  RefreshCw,
  Clock,
  Calendar,
  User,
  HeartPulse,
} from 'lucide-react';
import PacsStudySyncModal from '../PacsStudySyncModal';
import { useComprehensivePacsSync } from '../../../helpers/api/pacs/pacs';
import { usePermissions } from '../../../helpers/acl/permissions';
import { Modal } from '../../ComponnentsUI/common/Modal';
import { Button } from '../../ComponnentsUI/common/Button';
import { PacsSyncWizard } from '../../ComponnentsUI/NewCaseWizard/PacsSyncWizard';

// --- INTERFACES (No changes needed, kept for context) ---
interface StudyData {
  study_instance_uid: string;
  study_date: string;
  study_mode: string;
  is_locked: boolean;
  category?: string;
  serie?: Array<{
    created_at: string;
    updated_at: string | null;
    serie_instance_uid: string;
    study_instance_uid: string;
    type: string;
  }>;
  pregnancy?: {
    id: number;
    patient_id: string;
    estimated_due_date: string;
    last_menstrual_period: string;
    gestational_age_weeks: number;
    gestational_age_days: number;
    pregnancy_type: string;
    dating_method: string;
    fetal_presentation: string;
    planned_delivery_mode: string;
    pregnancy_status: string;
    closed_at?: string | null;
    outcome_date?: string | null;
    created_at: string;
    updated_at: string | null;
  };
  patient?: {
    id: string;
    patient_name: string;
    patient_last_name: string;
    birth_date: string;
  };
  user?: {
    id: string;
    first_name: string;
    last_name: string;
  };
}

interface InstanceData {
  id: number;
  sopInstanceUID: string;
  sopClassUID: string;
  seriesInstanceUID: string;
  instanceNumber: string;
  transferSyntax: string;
  pixelSpacing: string;
  numberOfFrames: number;
  receivedAt: string;
  synced: boolean;
  syncAttemptedAt: string | null;
  syncError: string | null;
}

interface PacsStudyDetailsData {
  status: string;
  message: string;
  data: {
    study: StudyData | null;
    id: number;
    studyInstanceUID: string;
    patientId: string | null;
    patientName: string;
    patientBirthDate: string;
    studyDate: string;
    studyTime: string;
    studyDescription: string | null;
    modality: string;
    referringPhysician: string | null;
    accessionNumber: string | null;
    lastMenstrualPeriod: string | null;
    numberOfInstances: number;
    receivedAt: string;
    sourceAeTitle: string;
    aeType: string;
    fullySync: boolean;
    syncedInstanceCount: number;
    unsyncedInstanceCount: number;
    instances: InstanceData[];
  };
  datetime: string;
  status_code: number;
}

interface PacsStudy {
  studyInstanceUID: string;
  patientName: string;
  studyDate: string;
  studyTime: string;
  aeType: string;
  modality: string;
  numberOfInstances: number;
  lastMenstrualPeriod?: string;
}

interface PacsStudyDetailsModalProps {
  isModalOpen: boolean;
  handleCancel: () => void;
  setIsModalOpen: (isModalOpen: boolean) => void;
  selectedPacsStudy: PacsStudy;
  pacsStudyDetails?: PacsStudyDetailsData;
  onSyncSuccess?: () => void;
  onRefetchStudyDetails?: () => void;
}

// Helper component for displaying data fields cleanly
const InfoField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => {
  const { t } = useTranslation('ExamManagement');
  return (
    <div>
      <label className="block text-xs font-medium text-title-page-color-300 dark:text-gray-400 mb-1">
        {label}
      </label>
      <div className="text-sm text-title-page-color-100 dark:text-white bg-card-color-200 dark:bg-gray-700 p-2.5 rounded-md break-words">
        {value || t('common.notAvailable')}
      </div>
    </div>
  );
};

const PacsStudyDetailsModal = ({
  isModalOpen,
  handleCancel,
  setIsModalOpen,
  selectedPacsStudy,
  pacsStudyDetails,
  onSyncSuccess,
  onRefetchStudyDetails,
}: PacsStudyDetailsModalProps) => {
  const { t } = useTranslation('ExamManagement');
  const [showSyncModal, setShowSyncModal] = useState(false);
  const { can } = usePermissions();
  const canSync = can('pacs_device.sync'); // import incoming DICOM into the system
  const { isPending: isSyncing } = useComprehensivePacsSync(); // Removed 'mutate' as it wasn't used here
  const notAvailable = t('common.notAvailable');

  const handleOpenSyncModal = () => setShowSyncModal(true);
  const handleCloseSyncModal = () => setShowSyncModal(false);

  const handleSyncComplete = () => {
    setShowSyncModal(false);
    setIsModalOpen(false);
    onSyncSuccess?.();
    onRefetchStudyDetails?.();
  };

  const formatTime = (timeString: string | undefined) => {
    if (!timeString || timeString.length < 6) return notAvailable;
    const hours = timeString.substring(0, 2);
    const minutes = timeString.substring(2, 4);
    const seconds = timeString.substring(4, 6);
    return `${hours}:${minutes}:${seconds}`;
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return notAvailable;
    try {
      if (dateString.includes('T')) {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      }
      if (dateString.length === 8) {
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        const date = new Date(`${year}-${month}-${day}T00:00:00`); // Add time to avoid timezone issues
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      }
      return dateString;
    } catch (error) {
      return dateString; // Fallback for unexpected formats
    }
  };

  const existingStudy = pacsStudyDetails?.data?.study;
  const unsyncedCount = pacsStudyDetails?.data?.unsyncedInstanceCount || 0;
  const syncedCount = pacsStudyDetails?.data?.syncedInstanceCount || 0;

  return (
    <>
      <Modal
        isOpen={isModalOpen && !showSyncModal}
        onClose={handleCancel}
        size="5xl"
      >
        <Modal.Header
          title={t('pacsModal.title')}
          onClose={handleCancel}
        />
        <Modal.Body className="p-6 space-y-6 bg-card-color-200 dark:bg-gray-900 h-[555px] overflow-y-auto">
          {!pacsStudyDetails ? (
            <div className="flex flex-col items-center justify-center h-96">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-base font-medium text-title-page-color-300 dark:text-gray-300">
                {t('pacsModal.loading')}
              </p>
            </div>
          ) : (
            <>
              {/* Section 1: PACS Study Information */}
              <div className="bg-card-color-100 dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                <h3 className="text-lg font-semibold text-title-page-color-100 dark:text-white mb-4 flex items-center">
                  <Server className="h-5 w-5 mr-3 text-blue-600" />
                  {t('pacsModal.pacsStudyInformation')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                  <InfoField
                    label={t('pacsModal.patientName')}
                    value={
                      pacsStudyDetails.data.patientName?.replace('^', ' ') ||
                      notAvailable
                    }
                  />
                  <InfoField
                    label={t('pacsModal.patientBirthDate')}
                    value={formatDate(pacsStudyDetails.data.patientBirthDate)}
                  />
                  <InfoField
                    label={t('pacsModal.studyDate')}
                    value={formatDate(pacsStudyDetails.data.studyDate)}
                  />
                  <InfoField
                    label={t('pacsModal.studyTime')}
                    value={formatTime(pacsStudyDetails.data.studyTime)}
                  />
                  <div className="md:col-span-2">
                    <InfoField
                      label={t('pacsModal.studyInstanceUid')}
                      value={
                        <span>{pacsStudyDetails.data.studyInstanceUID}</span>
                      }
                    />
                  </div>
                  {pacsStudyDetails.data.studyDescription && (
                    <div className="md:col-span-2">
                      <InfoField
                        label={t('pacsModal.description')}
                        value={pacsStudyDetails.data.studyDescription}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Section 2: Existing Study Information (if available) */}
              {existingStudy && (
                <div className="bg-card-color-100 dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                  <h3 className="text-lg font-semibold text-title-page-color-100 dark:text-white mb-4 flex items-center">
                    <CheckCircle className="h-5 w-5 mr-3 text-green-600" />
                    {t('pacsModal.existingStudyInSystem')}
                  </h3>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                      <InfoField
                        label={t('pacsModal.patientName')}
                        value={`${existingStudy.patient?.patient_name || ''} ${existingStudy.patient?.patient_last_name || ''}`}
                      />
                      <InfoField
                        label={t('pacsModal.dateOfBirth')}
                        value={formatDate(existingStudy.patient?.birth_date)}
                      />
                      <InfoField
                        label={t('pacsModal.studyCategory')}
                        value={
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400">
                            {existingStudy.category || t('common.notSet')}
                          </span>
                        }
                      />
                      <InfoField
                        label={t('pacsModal.uploadedBy')}
                        value={`${existingStudy.user?.first_name || ''} ${existingStudy.user?.last_name || ''}`}
                      />
                    </div>
                    {existingStudy.pregnancy && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <h4 className="text-md font-medium text-title-page-color-300 dark:text-gray-400 mb-3">
                          {t('pacsModal.pregnancyInformation')}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                          <InfoField
                            label={t('pacsModal.gestationalAge')}
                            value={
                              <span className="font-bold text-blue-700 dark:text-blue-400">
                                {t('pacsModal.gestationalAgeValue', {
                                  weeks:
                                    existingStudy.pregnancy
                                      .gestational_age_weeks,
                                  days: existingStudy.pregnancy
                                    .gestational_age_days,
                                })}
                              </span>
                            }
                          />
                          <InfoField
                            label={t('pacsModal.estimatedDueDate')}
                            value={formatDate(
                              existingStudy.pregnancy.estimated_due_date
                            )}
                          />
                          <InfoField
                            label={t('pacsModal.lastMenstrualPeriod')}
                            value={formatDate(
                              existingStudy.pregnancy.last_menstrual_period
                            )}
                          />
                          <InfoField
                            label={t('pacsModal.pregnancyType')}
                            value={existingStudy.pregnancy.pregnancy_type}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Section 3: Instance Sync Status */}
              <div className="bg-card-color-100 dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                <h3 className="text-lg font-semibold text-title-page-color-100 dark:text-white mb-4">
                  {t('pacsModal.instanceSyncStatus')}
                </h3>
                <div className="space-y-4">
                  {unsyncedCount > 0 && (
                    <div className="flex items-center justify-between p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <AlertCircle className="h-7 w-7 text-orange-500 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                            {t('pacsModal.unsyncedInstances')}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {t('pacsModal.unsyncedHint')}
                          </p>
                        </div>
                      </div>
                      <span className="text-xl font-bold text-orange-600 dark:text-orange-400">
                        {unsyncedCount}
                      </span>
                    </div>
                  )}
                  {syncedCount > 0 && (
                    <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <CheckCircle className="h-7 w-7 text-green-600 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                            {t('pacsModal.syncedInstances')}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {t('pacsModal.syncedHint')}
                          </p>
                        </div>
                      </div>
                      <span className="text-3xl font-bold text-green-600 dark:text-green-400">
                        {syncedCount}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div className="w-full flex justify-between items-center">
            <Button variant="secondary" onClick={handleCancel}>
              {t('pacsModal.close')}
            </Button>

            {canSync && !pacsStudyDetails?.data?.fullySync && unsyncedCount > 0 && (
              <Button
                variant="primary"
                onClick={handleOpenSyncModal}
                disabled={isSyncing}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`}
                />
                {isSyncing
                  ? t('pacsModal.syncing')
                  : t('pacsModal.syncInstances', { count: unsyncedCount })}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      {showSyncModal && canSync && (
        // <PacsStudySyncModal
        //   isModalOpen={showSyncModal}
        //   handleCancel={handleCloseSyncModal}
        //   setIsModalOpen={setShowSyncModal}
        //   selectedPacsStudy={selectedPacsStudy}
        //   pacsStudyDetails={pacsStudyDetails}
        //   onSyncSuccess={handleSyncComplete}
        // />
        <PacsSyncWizard
          isOpen={showSyncModal}
          onClose={handleCloseSyncModal}
          selectedPacsStudy={selectedPacsStudy}
          pacsStudyDetails={pacsStudyDetails}
          onSyncSuccess={handleSyncComplete}
        />
      )}
    </>
  );
};

export default PacsStudyDetailsModal;
